import { ApplicationType, isAdminApplicationType } from './event';
import { IPageOption } from './ui';
import { IUser } from './user';

export type EnrollResponseItemType = {
  id: number;
  response: string;
};

export type EnrollResponseType = {
  additional: EnrollResponseItemType[];
  feedback: EnrollResponseItemType[];
};

export const ATTENDANCE_STATUS = [
  'PRESENT',
  'ABSENT',
  'LATE',
  'CANCEL',
  'UNDETERMINED',
] as const;

export type AttendanceStatusType = (typeof ATTENDANCE_STATUS)[number];

export const SELECTION_STATUS = [
  'APPLIED',
  'SELECTED',
  'NOT_SELECTED',
] as const;

export type SelectionStatusType = (typeof SELECTION_STATUS)[number];

export interface IEnroll {
  id: number;
  applicationType?: ApplicationType;
  attendanceStatus: AttendanceStatusType;
  createdAt: string;
  enrollDate: string;
  lastParticipationDate?: string;
  response?: ResponseType | null;
  updatedAt: string;
  user: IUser;
  selectionStatus: SelectionStatusType | null;
}

export interface IEnrollUser {
  id: number;
}

export type SearchEnrollQueryType = IPageOption & {
  eventId: number;
  /** 참여자 이름 검색 */
  name?: string;
  /** 다중 선택이라 콤마로 묶어 보낸다. 미전달이면 전체 */
  attendanceStatus?: string;
  selectionStatus?: string;
  /** 참여 확정 모달용 — 어드민 수기 추가(유형이 붙은 건)를 제외한다 */
  withoutApplicationType?: boolean;
};
export type SearchEnrollQueryByUserType = IPageOption & { userId: number };

export interface IEnrollUserCandidate {
  id: number;
  name: string;
  /** 마스킹된 번호 (010****5678) */
  phone: string | null;
}

export type SearchUserCandidatesQueryType = IPageOption & {
  eventId: number;
  name?: string;
};

export type EnrollResponseDataType = {
  userId: number;
  enrollId: number;
};

/** 랜덤 추첨 요청 — 뽑기만 하고 저장하지 않는다 */
export interface IDrawRandom {
  eventId: number;
  count: number;
  /** 후보 상태. 검색과 같이 콤마로 묶어 보낸다 */
  selectionStatus: string;
}

export interface IConfirmSelection {
  eventId: number;
  enrollIds: number[];
}

export interface IDeleteEnroll {
  enrollIds: number[];
}

export interface IUpdateAttendanceStatus {
  enrollIds: number[];
  attendanceStatus: AttendanceStatusType;
}

export interface IEnrollResponse {
  user: IUser;
  additional?: EnrollResponseItemType[];
  feedback?: EnrollResponseItemType[];
  enroll?: IEnroll;
}

export interface IEnrollSummary {
  totalEnrollCount: string;
  feedbackCount: string;
  lateCount: string;
  absentCount: string;
  cancelCount: string;
  /** 추첨형 선정 결과 — 선착순은 전부 0 */
  selectedCount: string;
  notSelectedCount: string;
  appliedCount: string;
  /** 추첨으로 뽑힌 인원만 — 어드민 지정(운영진 등)은 빠진다 */
  selectedGeneralCount: string;
  applicationTypeCounts?: {
    applicationType: ApplicationType | null;
    count: string;
  }[];
}

/**
 * 어드민이 수기로 추가한 인원(운영진/사진/기타/일반(시스템추가))을 뺀 순수 신청자 .
 */
export const getGeneralEnrollCount = (summary?: IEnrollSummary) => {
  const adminAdded = (summary?.applicationTypeCounts || []).reduce(
    (acc, { applicationType, count }) =>
      isAdminApplicationType(applicationType) ? acc + Number(count || 0) : acc,
    0,
  );

  return Math.max(0, Number(summary?.totalEnrollCount || 0) - adminAdded);
};
